import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { InputTitle, InvestmentOpportunitiesSchema } from "./helper";
import InputController from "@components/FormControllers/controllers/InputController";
import SelectController from "@components/FormControllers/controllers/SelectController";
import UploaderController from "@components/FormControllers/Uploader/UploaderController";
import BtnBase from "@components/Buttons/BtnBase";
import useTranslation from "next-translate/useTranslation";
import MapRender from "@components/google-map/MapRender";
import { Text } from "@utils/helper";
import { usePartnerSubmitHandler } from "Hooks/usePartnerSubmitHandler";

export default function ProvideAreal({ RealEstateOptions }) {
  const { t } = useTranslation("submit-opportunities");
  const { submitHandler, isLoading } = usePartnerSubmitHandler();
  const [selected, onSelect] = useState({
    zoom: 13,
    place: {
      lat: 24.712590112894198,
      lng: 46.67388889383538,
    },
  });

  const { control, handleSubmit, setValue, setError } = useForm({
    resolver: yupResolver(InvestmentOpportunitiesSchema),
  });
  useEffect(() => {
    const place = selected?.place;
    const map_url = `https://www.google.com/maps/@${place?.lat},${place?.lng},${selected?.zoom}z?hl=ar`;
    setValue("property_location", map_url);
  }, [selected]);
  return (
    <form
      className="flex flex-col w-full bg-white relative rounded-[10px] z-10 -top-1 p-3 md:p-10 gap-3 md:gap-10 shadow-[0px_8px_16px_0px_#00000005]"
      onSubmit={handleSubmit(async (values) => {
        await submitHandler(
          values,
          1,
          {
            setError,
          },
          ["copy_of_property_ownership"]
        );
      })}
    >
      <div className="flex justify-between gap-6 flex-col md:flex-row">
        <div className="flex w-full flex-col gap-2 sm:gap-4">
          <InputTitle title="ProvideAreal.first_name" />
          <InputController
            removeNumbers
            control={control}
            name="first_name"
            className="bg-[#F8FBFF]"
            inputSize="16px"
            placeholder={t("placeholders:first_name")}
          />
        </div>
        <div className="flex w-full flex-col gap-2 sm:gap-4">
          <InputTitle title="ProvideAreal.last_name" />
          <InputController
            removeNumbers
            control={control}
            name="last_name"
            className="bg-[#F8FBFF]"
            inputSize="16px"
            placeholder={t("placeholders:last_name")}
          />
        </div>
      </div>
      <div className="flex justify-between gap-6 flex-col md:flex-row">
        <div className="flex w-full flex-col gap-2 sm:gap-4">
          <InputTitle title="ProvideAreal.company_name" />
          <InputController
            control={control}
            name="company_name"
            className="bg-[#F8FBFF]"
            inputSize="16px"
            placeholder={t("placeholders:company_name")}
          />
        </div>
        <div className="flex w-full flex-col gap-2 sm:gap-4">
          <InputTitle title="ProvideAreal.phone_number" />
          <InputController
            removeText
            control={control}
            name="phone_number"
            className="bg-[#F8FBFF]"
            inputConstrains={{ maxLength: 9 }}
            inputSize="16px"
            placeholder={t("placeholders:phone")}
          />
        </div>
      </div>

      <div className="flex justify-between gap-6 flex-col md:flex-row">
        <div className="flex w-full flex-col gap-2 sm:gap-4">
          <InputTitle title="ProvideAreal.email" />
          <InputController
            control={control}
            name="email"
            className="bg-[#F8FBFF]"
            removeSpaces
            inputSize="16px"
            placeholder={t("placeholders:email")}
          />
        </div>
        <div className="flex w-full flex-col gap-2 sm:gap-4">
          <InputTitle title="ProvideAreal.you_are" />
          <SelectController
            placeholder={t("placeholders:select")}
            control={control}
            name="you_are"
            className="bg-[#F8FBFF]"
            options={RealEstateOptions?.RealEstateYouAre}
          />
        </div>
      </div>
      <div className="flex justify-between gap-6 flex-col md:flex-row">
        <div className="flex w-full flex-col gap-5">
          <InputTitle title="ProvideAreal.property_type" />
          <SelectController
            placeholder={t("placeholders:select")}
            control={control}
            name="property_type"
            className="bg-[#F8FBFF]"
            options={RealEstateOptions?.RealEstatePropertyType}
          />
        </div>
        <div className="flex w-full flex-col gap-5">
          <InputTitle title="ProvideAreal.property_size" />
          <InputController
            control={control}
            name="property_size"
            className="bg-[#F8FBFF]"
            inputSize="16px"
            placeholder={t("placeholders:add_answer")}
          />
        </div>
      </div>
      <div className="flex justify-between gap-6 flex-col md:flex-row">
        <div className="flex w-full flex-col gap-5">
          <InputTitle title="ProvideAreal.city" />
          <InputController
            control={control}
            name="city"
            className="bg-[#F8FBFF]"
            inputSize="16px"
            placeholder={t("placeholders:add_answer")}
          />
        </div>
        <div className="flex w-full flex-col gap-5">
          <InputTitle title="ProvideAreal.district" />
          <InputController
            control={control}
            name="district"
            className="bg-[#F8FBFF]"
            inputSize="16px"
            placeholder={t("placeholders:add_answer")}
          />
        </div>
      </div>
      <div className="flex w-full flex-col gap-2 sm:gap-4">
        <InputTitle title="ProvideAreal.property_location" />
        <InputController
          control={control}
          name="property_location"
          className="bg-[#F8FBFF]"
          inputSize="16px"
        />
        <MapRender selected={selected} onSelect={onSelect} />
      </div>
      <div className="flex w-full flex-col gap-2 sm:gap-4">
        <div className="!text-black !text-[12px] sm:!text-[16px]">
          {t("ProvideAreal.property_ownership")}
        </div>
        <UploaderController
          control={control}
          name="copy_of_property_ownership"
          variant="custom"
          label={t("placeholders:attach")}
          accept=".jpg, .pdf, .png"
        />
      </div>
      <div className="flex w-full flex-col gap-2 sm:gap-4">
        <div className="!text-black !text-[12px] sm:!text-[16px]">
          {t("ProvideAreal.price_per_meter")}
        </div>
        <InputController
          control={control}
          name="price_per_meter"
          className="bg-[#F8FBFF]"
          type="number"
          inputProps={{
            type: "number",
          }}
          inputSize="16px"
          placeholder={t("placeholders:add_answer")}
        />
      </div>
      <div className="flex w-full flex-col gap-2 sm:gap-4">
        <InputTitle title="ProvideAreal.comment" />
        <InputController
          control={control}
          name="comment"
          className="bg-[#F8FBFF]"
          rows={5}
          multiline
          inputSize="16px"
          inputStyles={{ height: "auto !important" }}
          placeholder={t("placeholders:add_answer")}
        />
      </div>
      <div className="pt-2.5">
        <BtnBase
          disabled={isLoading}
          isLoading={isLoading}
          variant="contained"
          type="submit"
          fullWidth
          className="!rounded-3xl !py-3 !capitalize"
        >
          <Text color="white" className="!top-px leading-normal">
            {t("common-locale:actions.submit")}
          </Text>
        </BtnBase>
      </div>
    </form>
  );
}
